import { DiagnosticFormData } from '../../../types/diagnostic';
import { motion } from 'framer-motion';
import { dpsTheme } from '../../../styles/dpsTheme';
import { Home, Key, DoorOpen, ParkingSquare, Building2, Info } from 'lucide-react';
import { useDeviceSize } from '../../../hooks/useMediaQuery';
import { useAuth } from '../../../contexts/AuthContext';

const STAFF_ROLES = ['advisor', 'company_admin', 'super_admin'];

interface HousingTypeProps {
  formData: DiagnosticFormData;
  updateFormData: (data: Partial<DiagnosticFormData>) => void;
}

export default function HousingType({ formData, updateFormData }: HousingTypeProps) {
  const { isMobile } = useDeviceSize();
  const { userProfile } = useAuth();
  const isStaff = STAFF_ROLES.includes(userProfile?.role ?? '');
  const isValid = formData.housingType && formData.occupancyStatus && formData.floors >= 0;

  return (
    <div>
      <h2 style={{
        fontSize: dpsTheme.typography.fontSize['3xl'],
        fontWeight: dpsTheme.typography.fontWeight.bold,
        color: dpsTheme.colors.text.primary,
        marginBottom: dpsTheme.spacing['2xl']
      }}>
        Caractéristiques de votre logement
      </h2>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: dpsTheme.spacing['3xl'] }}>

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        {/* RÉFÉRENCE INTERNE — staff uniquement */}
        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        {isStaff && (
          <div style={{
            padding: dpsTheme.spacing.lg,
            background: 'rgba(139, 92, 246, 0.08)',
            border: '1px solid rgba(139, 92, 246, 0.25)',
            borderRadius: dpsTheme.borderRadius.md
          }}>
            <label style={{
              display: 'block',
              fontSize: dpsTheme.typography.fontSize.sm,
              fontWeight: dpsTheme.typography.fontWeight.semibold,
              color: '#a78bfa',
              marginBottom: dpsTheme.spacing.sm
            }}>
              Référence interne (optionnel)
            </label>
            <input
              type="text"
              maxLength={100}
              value={formData.clientReference ?? ''}
              onChange={(e) => updateFormData({ clientReference: e.target.value })}
              placeholder="Ex: CLIENT-2026-001"
              style={{
                width: '100%',
                padding: dpsTheme.spacing.md,
                borderRadius: dpsTheme.borderRadius.sm,
                border: `1px solid rgba(139, 92, 246, 0.35)`,
                background: dpsTheme.colors.background.darker,
                color: dpsTheme.colors.text.primary,
                fontSize: dpsTheme.typography.fontSize.base,
                boxSizing: 'border-box'
              }}
            />
            <p style={{
              fontSize: dpsTheme.typography.fontSize.xs,
              color: dpsTheme.colors.text.muted,
              marginTop: dpsTheme.spacing.xs,
              marginBottom: 0
            }}>
              Ce code vous permettra de retrouver ce diagnostic dans votre CRM. Il n'est pas visible par le client.
            </p>
          </div>
        )}

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        {/* VOTRE LOGEMENT */}
        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        <div>
          <h3 style={{
            fontSize: dpsTheme.typography.fontSize.xl,
            fontWeight: dpsTheme.typography.fontWeight.semibold,
            color: dpsTheme.colors.text.primary,
            marginBottom: dpsTheme.spacing.lg,
            paddingBottom: dpsTheme.spacing.sm,
            borderBottom: `2px solid ${dpsTheme.colors.border.primary}`
          }}>
            Votre logement
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: dpsTheme.spacing.xl }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: dpsTheme.typography.fontSize.md,
                fontWeight: dpsTheme.typography.fontWeight.semibold,
                color: dpsTheme.colors.text.secondary,
                marginBottom: dpsTheme.spacing.md
              }}>
                Type de bien
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: dpsTheme.spacing.lg }}>
                {['house', 'apartment'].map((type) => {
                  const isSelected = formData.housingType === type;
                  return (
                    <motion.button
                      key={type}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        updateFormData({
                          housingType: type as 'house' | 'apartment',
                          hasAccessibleGarden: type === 'apartment' ? false : formData.hasAccessibleGarden,
                          garageConnectedToHome: type === 'apartment' ? formData.garageConnectedToHome : null,
                          apartmentAccessType: type === 'apartment' ? formData.apartmentAccessType : ''
                        });
                      }}
                      style={{
                        padding: isMobile ? dpsTheme.spacing.lg : dpsTheme.spacing.xl,
                        borderRadius: dpsTheme.borderRadius.md,
                        border: `2px solid ${isSelected ? dpsTheme.colors.accent.blue.primary : dpsTheme.colors.border.primary}`,
                        background: isSelected ? dpsTheme.colors.accent.blue.bg : dpsTheme.colors.background.darker,
                        color: isSelected ? dpsTheme.colors.accent.blue.primary : dpsTheme.colors.text.secondary,
                        fontSize: isMobile ? dpsTheme.typography.fontSize.base : dpsTheme.typography.fontSize.lg,
                        fontWeight: dpsTheme.typography.fontWeight.semibold,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        backdropFilter: 'blur(10px)',
                        minHeight: isMobile ? '60px' : 'auto'
                      }}
                    >
                      {type === 'house' ? 'Maison' : 'Appartement'}
                    </motion.button>
                  );
                })}
              </div>
            </div>

            {formData.housingType === 'apartment' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                style={{
                  padding: dpsTheme.spacing.lg,
                  backgroundColor: dpsTheme.colors.background.darker,
                  borderRadius: dpsTheme.borderRadius.md,
                  border: `1px solid ${dpsTheme.colors.border.primary}`
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: dpsTheme.spacing.sm,
                  marginBottom: dpsTheme.spacing.md,
                  padding: dpsTheme.spacing.sm,
                  backgroundColor: dpsTheme.colors.accent.blue.bg,
                  borderRadius: dpsTheme.borderRadius.sm
                }}>
                  <Info size={16} style={{ color: dpsTheme.colors.accent.blue.primary, flexShrink: 0 }} />
                  <p style={{
                    fontSize: dpsTheme.typography.fontSize.xs,
                    color: dpsTheme.colors.text.secondary,
                    margin: 0
                  }}>
                    Cette information nous permet d'adapter nos recommandations à vos possibilités d'installation
                  </p>
                </div>

                <label style={{
                  display: 'block',
                  fontSize: dpsTheme.typography.fontSize.md,
                  fontWeight: dpsTheme.typography.fontWeight.semibold,
                  color: dpsTheme.colors.text.secondary,
                  marginBottom: dpsTheme.spacing.md
                }}>
                  Type d'accès à votre appartement
                </label>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: dpsTheme.spacing.lg
                }}>
                  {[
                    {
                      value: 'direct',
                      Icon: DoorOpen,
                      title: 'Accès direct depuis la rue',
                      description: 'Rez-de-chaussée ou maison de ville avec entrée privée'
                    },
                    {
                      value: 'common_areas',
                      Icon: Building2,
                      title: 'Accès par parties communes',
                      description: 'Hall d\'immeuble, cage d\'escalier, coursive'
                    }
                  ].map((option) => {
                    const isSelected = formData.apartmentAccessType === option.value;
                    return (
                      <motion.button
                        key={option.value}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => updateFormData({ apartmentAccessType: option.value as 'direct' | 'common_areas' })}
                        style={{
                          padding: isMobile ? dpsTheme.spacing.md : dpsTheme.spacing.lg,
                          borderRadius: dpsTheme.borderRadius.md,
                          border: `2px solid ${isSelected ? dpsTheme.colors.accent.blue.primary : dpsTheme.colors.border.primary}`,
                          background: isSelected ? dpsTheme.colors.accent.blue.bg : dpsTheme.colors.background.primary,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          textAlign: 'center',
                          gap: dpsTheme.spacing.md,
                          minHeight: isMobile ? '140px' : '160px'
                        }}
                      >
                        <option.Icon
                          size={isMobile ? 24 : 32}
                          style={{
                            color: isSelected ? dpsTheme.colors.accent.blue.primary : dpsTheme.colors.text.secondary,
                            flexShrink: 0
                          }}
                        />
                        <div>
                          <div style={{
                            fontSize: isMobile ? dpsTheme.typography.fontSize.sm : dpsTheme.typography.fontSize.base,
                            fontWeight: dpsTheme.typography.fontWeight.semibold,
                            color: isSelected ? dpsTheme.colors.accent.blue.primary : dpsTheme.colors.text.secondary,
                            marginBottom: dpsTheme.spacing.xs
                          }}>
                            {option.title}
                          </div>
                          <div style={{
                            fontSize: isMobile ? dpsTheme.typography.fontSize.xs : dpsTheme.typography.fontSize.sm,
                            color: dpsTheme.colors.text.muted,
                            lineHeight: '1.4'
                          }}>
                            {option.description}
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            <div>
              <label style={{
                display: 'block',
                fontSize: dpsTheme.typography.fontSize.md,
                fontWeight: dpsTheme.typography.fontWeight.semibold,
                color: dpsTheme.colors.text.secondary,
                marginBottom: dpsTheme.spacing.md
              }}>
                Statut d'occupation
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: dpsTheme.spacing.lg }}>
                {[
                  { value: 'owner', label: 'Propriétaire', Icon: Home },
                  { value: 'tenant', label: 'Locataire', Icon: Key }
                ].map((option) => {
                  const isSelected = formData.occupancyStatus === option.value;
                  return (
                    <motion.button
                      key={option.value}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        updateFormData({
                          occupancyStatus: option.value as 'owner' | 'tenant'
                        });
                      }}
                      style={{
                        padding: isMobile ? dpsTheme.spacing.lg : dpsTheme.spacing.xl,
                        borderRadius: dpsTheme.borderRadius.md,
                        border: `2px solid ${isSelected ? dpsTheme.colors.accent.blue.primary : dpsTheme.colors.border.primary}`,
                        background: isSelected ? dpsTheme.colors.accent.blue.bg : dpsTheme.colors.background.darker,
                        color: isSelected ? dpsTheme.colors.accent.blue.primary : dpsTheme.colors.text.secondary,
                        fontSize: isMobile ? dpsTheme.typography.fontSize.base : dpsTheme.typography.fontSize.lg,
                        fontWeight: dpsTheme.typography.fontWeight.semibold,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        backdropFilter: 'blur(10px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: dpsTheme.spacing.sm,
                        minHeight: isMobile ? '60px' : 'auto'
                      }}
                    >
                      <option.Icon size={isMobile ? 20 : 24} />
                      {option.label}
                    </motion.button>
                  );
                })}
              </div>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: dpsTheme.typography.fontSize.md,
                fontWeight: dpsTheme.typography.fontWeight.semibold,
                color: dpsTheme.colors.text.secondary,
                marginBottom: dpsTheme.spacing.sm
              }}>
                Nombre d'étages
                <span style={{
                  fontSize: dpsTheme.typography.fontSize.xs,
                  color: dpsTheme.colors.text.muted,
                  marginLeft: '8px'
                }}>
                  (0 pour plain-pied)
                </span>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: dpsTheme.spacing.md }}>
                <button
                  type="button"
                  onClick={() => updateFormData({ floors: Math.max(0, formData.floors - 1) })}
                  style={{
                    padding: dpsTheme.spacing.md,
                    borderRadius: dpsTheme.borderRadius.sm,
                    border: `1px solid ${dpsTheme.colors.border.primary}`,
                    background: dpsTheme.colors.background.darker,
                    color: dpsTheme.colors.text.secondary,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                </button>
                <input
                  type="number"
                  min="0"
                  max="50"
                  value={formData.floors}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 0) {
                      updateFormData({ floors: value });
                    }
                  }}
                  style={{
                    width: '80px',
                    textAlign: 'center',
                    padding: dpsTheme.spacing.md,
                    borderRadius: dpsTheme.borderRadius.sm,
                    border: `1px solid ${dpsTheme.colors.border.primary}`,
                    background: dpsTheme.colors.background.darker,
                    color: dpsTheme.colors.text.primary,
                    fontSize: dpsTheme.typography.fontSize.lg,
                    fontWeight: dpsTheme.typography.fontWeight.semibold
                  }}
                />
                <button
                  type="button"
                  onClick={() => updateFormData({ floors: Math.min(50, formData.floors + 1) })}
                  style={{
                    padding: dpsTheme.spacing.md,
                    borderRadius: dpsTheme.borderRadius.sm,
                    border: `1px solid ${dpsTheme.colors.border.primary}`,
                    background: dpsTheme.colors.background.darker,
                    color: dpsTheme.colors.text.secondary,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        {/* PROFIL D'OCCUPATION */}
        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        <div>
          <h3 style={{
            fontSize: dpsTheme.typography.fontSize.xl,
            fontWeight: dpsTheme.typography.fontWeight.semibold,
            color: dpsTheme.colors.text.primary,
            marginBottom: dpsTheme.spacing.lg,
            paddingBottom: dpsTheme.spacing.sm,
            borderBottom: `2px solid ${dpsTheme.colors.border.primary}`
          }}>
            Profil d'occupation
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: dpsTheme.spacing.lg }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: dpsTheme.typography.fontSize.sm,
                fontWeight: dpsTheme.typography.fontWeight.semibold,
                color: dpsTheme.colors.text.secondary,
                marginBottom: dpsTheme.spacing.sm
              }}>
                Type d'occupation du logement
              </label>
              <select
                value={formData.occupationType || ''}
                onChange={(e) => updateFormData({ occupationType: e.target.value as any })}
                style={{
                  width: '100%',
                  padding: dpsTheme.spacing.md,
                  borderRadius: dpsTheme.borderRadius.sm,
                  border: `1px solid ${dpsTheme.colors.border.primary}`,
                  background: dpsTheme.colors.background.darker,
                  color: dpsTheme.colors.text.primary,
                  fontSize: dpsTheme.typography.fontSize.base,
                  cursor: 'pointer'
                }}
              >
                <option value="">Sélectionnez...</option>
                <option value="main_residence">Résidence principale (occupé en permanence)</option>
                <option value="secondary_residence">Résidence secondaire (occupé ponctuellement)</option>
                <option value="rental">Logement mis en location</option>
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: dpsTheme.typography.fontSize.sm,
                fontWeight: dpsTheme.typography.fontWeight.semibold,
                color: dpsTheme.colors.text.secondary,
                marginBottom: dpsTheme.spacing.sm
              }}>
                Composition du foyer
              </label>
              <select
                value={formData.householdComposition || ''}
                onChange={(e) => updateFormData({ householdComposition: e.target.value as any })}
                style={{
                  width: '100%',
                  padding: dpsTheme.spacing.md,
                  borderRadius: dpsTheme.borderRadius.sm,
                  border: `1px solid ${dpsTheme.colors.border.primary}`,
                  background: dpsTheme.colors.background.darker,
                  color: dpsTheme.colors.text.primary,
                  fontSize: dpsTheme.typography.fontSize.base,
                  cursor: 'pointer'
                }}
              >
                <option value="">Sélectionnez...</option>
                <option value="single">Personne seule</option>
                <option value="couple">Couple sans enfant</option>
                <option value="family">Famille avec enfant(s)</option>
                <option value="cohabitation">Cohabitation (colocation, intergénérationnel)</option>
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: dpsTheme.typography.fontSize.sm,
                fontWeight: dpsTheme.typography.fontWeight.semibold,
                color: dpsTheme.colors.text.secondary,
                marginBottom: dpsTheme.spacing.xs
              }}>
                À quelle fréquence votre logement est-il occupé ?
              </label>
              <p style={{
                fontSize: dpsTheme.typography.fontSize.xs,
                color: dpsTheme.colors.text.muted,
                marginBottom: dpsTheme.spacing.sm,
                marginTop: 0
              }}>
                Cela nous aide à évaluer le risque lié aux périodes d'absence
              </p>
              <select
                value={formData.presenceRhythm || ''}
                onChange={(e) => updateFormData({ presenceRhythm: e.target.value as any })}
                style={{
                  width: '100%',
                  padding: dpsTheme.spacing.md,
                  borderRadius: dpsTheme.borderRadius.sm,
                  border: `1px solid ${dpsTheme.colors.border.primary}`,
                  background: dpsTheme.colors.background.darker,
                  color: dpsTheme.colors.text.primary,
                  fontSize: dpsTheme.typography.fontSize.base,
                  cursor: 'pointer'
                }}
              >
                <option value="">Sélectionnez...</option>
                <option value="permanent">Occupé en permanence - Présence quotidienne, rarement vide plus de 24h</option>
                <option value="mostly_occupied">Occupé la plupart du temps - Absences régulières (travail, sorties) mais retours quotidiens</option>
                <option value="empty_daytime">Inoccupé en journée - Vide pendant les heures de travail (8h-18h)</option>
                <option value="frequent_absences">Absences fréquentes de plusieurs jours - Déplacements professionnels, weekends prolongés</option>
                <option value="occasional">Occupation occasionnelle - Résidence secondaire, occupée moins de 50% du temps</option>
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: dpsTheme.typography.fontSize.sm,
                fontWeight: dpsTheme.typography.fontWeight.semibold,
                color: dpsTheme.colors.text.secondary,
                marginBottom: dpsTheme.spacing.sm
              }}>
                Animal de compagnie dissuasif (ex : chien) présent au domicile ?
              </label>
              <div style={{ display: 'flex', gap: dpsTheme.spacing.lg }}>
                {[
                  { value: true, label: 'Oui' },
                  { value: false, label: 'Non' }
                ].map((option) => {
                  const isSelected = formData.hasPet === option.value;
                  return (
                    <motion.button
                      key={option.label}
                      type="button"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => updateFormData({ hasPet: option.value })}
                      style={{
                        flex: 1,
                        padding: dpsTheme.spacing.md,
                        borderRadius: dpsTheme.borderRadius.sm,
                        border: `2px solid ${isSelected ? dpsTheme.colors.accent.blue.primary : dpsTheme.colors.border.primary}`,
                        background: isSelected ? dpsTheme.colors.accent.blue.bg : dpsTheme.colors.background.darker,
                        color: isSelected ? dpsTheme.colors.accent.blue.primary : dpsTheme.colors.text.secondary,
                        fontSize: dpsTheme.typography.fontSize.base,
                        fontWeight: dpsTheme.typography.fontWeight.semibold,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {option.label}
                    </motion.button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        {/* CARACTÉRISTIQUES DE SÉCURITÉ */}
        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        <div>
          <h3 style={{
            fontSize: dpsTheme.typography.fontSize.xl,
            fontWeight: dpsTheme.typography.fontWeight.semibold,
            color: dpsTheme.colors.text.primary,
            marginBottom: dpsTheme.spacing.lg,
            paddingBottom: dpsTheme.spacing.sm,
            borderBottom: `2px solid ${dpsTheme.colors.border.primary}`
          }}>
            Caractéristiques de sécurité
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: dpsTheme.spacing.lg }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: dpsTheme.typography.fontSize.sm,
                fontWeight: dpsTheme.typography.fontWeight.semibold,
                color: dpsTheme.colors.text.secondary,
                marginBottom: dpsTheme.spacing.sm
              }}>
                Matériau de votre porte d'entrée
              </label>
              <select
                value={formData.doorMaterial || ''}
                onChange={(e) => updateFormData({ doorMaterial: e.target.value as any })}
                style={{
                  width: '100%',
                  padding: dpsTheme.spacing.md,
                  borderRadius: dpsTheme.borderRadius.sm,
                  border: `1px solid ${dpsTheme.colors.border.primary}`,
                  background: dpsTheme.colors.background.darker,
                  color: dpsTheme.colors.text.primary,
                  fontSize: dpsTheme.typography.fontSize.base,
                  cursor: 'pointer'
                }}
              >
                <option value="">Sélectionnez...</option>
                <option value="standard_wood">Bois standard (porte classique)</option>
                <option value="reinforced_wood">Bois renforcé (porte épaisse)</option>
                <option value="pvc">PVC</option>
                <option value="metal">Métal</option>
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: dpsTheme.typography.fontSize.sm,
                fontWeight: dpsTheme.typography.fontWeight.semibold,
                color: dpsTheme.colors.text.secondary,
                marginBottom: dpsTheme.spacing.xs
              }}>
                Type de vitrage de vos fenêtres
              </label>
              <p style={{
                fontSize: dpsTheme.typography.fontSize.xs,
                color: dpsTheme.colors.text.muted,
                marginBottom: dpsTheme.spacing.sm,
                marginTop: 0
              }}>
                Le vitrage influence la résistance à l'effraction
              </p>
              <select
                value={formData.windowType || ''}
                onChange={(e) => updateFormData({ windowType: e.target.value as any })}
                style={{
                  width: '100%',
                  padding: dpsTheme.spacing.md,
                  borderRadius: dpsTheme.borderRadius.sm,
                  border: `1px solid ${dpsTheme.colors.border.primary}`,
                  background: dpsTheme.colors.background.darker,
                  color: dpsTheme.colors.text.primary,
                  fontSize: dpsTheme.typography.fontSize.base,
                  cursor: 'pointer'
                }}
              >
                <option value="">Sélectionnez...</option>
                <option value="single_glazing">Simple vitrage (Très vulnérable)</option>
                <option value="double_glazing">Double vitrage standard (Isolation thermique)</option>
                <option value="reinforced_glazing">Double vitrage anti-effraction (Verre feuilleté retardateur)</option>
                <option value="triple_or_more">Triple vitrage ou supérieur (Haute performance)</option>
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: dpsTheme.typography.fontSize.sm,
                fontWeight: dpsTheme.typography.fontWeight.semibold,
                color: dpsTheme.colors.text.secondary,
                marginBottom: dpsTheme.spacing.sm
              }}>
                Vos fenêtres sont-elles équipées de volets ?
              </label>
              <select
                value={formData.hasShutters || ''}
                onChange={(e) => updateFormData({ hasShutters: e.target.value as any })}
                style={{
                  width: '100%',
                  padding: dpsTheme.spacing.md,
                  borderRadius: dpsTheme.borderRadius.sm,
                  border: `1px solid ${dpsTheme.colors.border.primary}`,
                  background: dpsTheme.colors.background.darker,
                  color: dpsTheme.colors.text.primary,
                  fontSize: dpsTheme.typography.fontSize.base,
                  cursor: 'pointer'
                }}
              >
                <option value="">Sélectionnez...</option>
                <option value="rolling">Oui, volets roulants</option>
                <option value="swing">Oui, volets battants</option>
                <option value="shutters">Oui, persiennes</option>
                <option value="none">Non, pas de volets</option>
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: dpsTheme.typography.fontSize.sm,
                fontWeight: dpsTheme.typography.fontWeight.semibold,
                color: dpsTheme.colors.text.secondary,
                marginBottom: dpsTheme.spacing.sm
              }}>
                Accès au logement (hors entrée principale)
              </label>
              <select
                value={formData.sideAccess || ''}
                onChange={(e) => updateFormData({ sideAccess: e.target.value as any })}
                style={{
                  width: '100%',
                  padding: dpsTheme.spacing.md,
                  borderRadius: dpsTheme.borderRadius.sm,
                  border: `1px solid ${dpsTheme.colors.border.primary}`,
                  background: dpsTheme.colors.background.darker,
                  color: dpsTheme.colors.text.primary,
                  fontSize: dpsTheme.typography.fontSize.base,
                  cursor: 'pointer'
                }}
              >
                <option value="">Sélectionnez...</option>
                <option value="none">Aucun accès latéral ou arrière</option>
                <option value="side_passage">Passage latéral accessible (mais sans issue)</option>
                <option value="hidden_access">Accès arrière/latéral dissimulé de la rue</option>
              </select>
            </div>
          </div>
        </div>

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        {/* ENVIRONNEMENT */}
        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        <div>
          <h3 style={{
            fontSize: dpsTheme.typography.fontSize.xl,
            fontWeight: dpsTheme.typography.fontWeight.semibold,
            color: dpsTheme.colors.text.primary,
            marginBottom: dpsTheme.spacing.lg,
            paddingBottom: dpsTheme.spacing.sm,
            borderBottom: `2px solid ${dpsTheme.colors.border.primary}`
          }}>
            Environnement
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: dpsTheme.spacing.lg }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: dpsTheme.typography.fontSize.sm,
                fontWeight: dpsTheme.typography.fontWeight.semibold,
                color: dpsTheme.colors.text.secondary,
                marginBottom: dpsTheme.spacing.sm
              }}>
                Éclairage public
              </label>
              <select
                value={formData.streetLighting || ''}
                onChange={(e) => updateFormData({ streetLighting: e.target.value as 'good' | 'medium' | 'poor' })}
                style={{
                  width: '100%',
                  padding: dpsTheme.spacing.md,
                  borderRadius: dpsTheme.borderRadius.sm,
                  border: `1px solid ${dpsTheme.colors.border.primary}`,
                  background: dpsTheme.colors.background.darker,
                  color: dpsTheme.colors.text.primary,
                  fontSize: dpsTheme.typography.fontSize.base,
                  cursor: 'pointer'
                }}
              >
                <option value="">Sélectionnez...</option>
                <option value="good">Bien éclairé toute la nuit</option>
                <option value="medium">Eclairage partiel ou coupures nocturnes</option>
                <option value="poor">Peu ou pas éclairé</option>
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: dpsTheme.typography.fontSize.sm,
                fontWeight: dpsTheme.typography.fontWeight.semibold,
                color: dpsTheme.colors.text.secondary,
                marginBottom: dpsTheme.spacing.sm
              }}>
                Visibilité depuis la rue
              </label>
              <select
                value={formData.streetVisibility || ''}
                onChange={(e) => updateFormData({ streetVisibility: e.target.value as 'high' | 'medium' | 'low' })}
                style={{
                  width: '100%',
                  padding: dpsTheme.spacing.md,
                  borderRadius: dpsTheme.borderRadius.sm,
                  border: `1px solid ${dpsTheme.colors.border.primary}`,
                  background: dpsTheme.colors.background.darker,
                  color: dpsTheme.colors.text.primary,
                  fontSize: dpsTheme.typography.fontSize.base,
                  cursor: 'pointer'
                }}
              >
                <option value="">Sélectionnez...</option>
                <option value="high">Très visible (proche de la rue)</option>
                <option value="medium">Partiellement visible</option>
                <option value="low">Peu visible (en retrait, végétation dense)</option>
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: dpsTheme.typography.fontSize.sm,
                fontWeight: dpsTheme.typography.fontWeight.semibold,
                color: dpsTheme.colors.text.secondary,
                marginBottom: dpsTheme.spacing.sm
              }}>
                Type de quartier
              </label>
              <select
                value={formData.neighborhoodType || ''}
                onChange={(e) => updateFormData({ neighborhoodType: e.target.value as 'residential' | 'mixed' | 'isolated' })}
                style={{
                  width: '100%',
                  padding: dpsTheme.spacing.md,
                  borderRadius: dpsTheme.borderRadius.sm,
                  border: `1px solid ${dpsTheme.colors.border.primary}`,
                  background: dpsTheme.colors.background.darker,
                  color: dpsTheme.colors.text.primary,
                  fontSize: dpsTheme.typography.fontSize.base,
                  cursor: 'pointer'
                }}
              >
                <option value="">Sélectionnez...</option>
                <option value="residential">Résidentiel</option>
                <option value="mixed">Mixte (résidentiel/commercial)</option>
                <option value="isolated">Isolé</option>
              </select>
            </div>
          </div>
        </div>

        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        {/* DÉPENDANCES */}
        {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: dpsTheme.spacing.lg }}>
          <motion.div
            style={{ display: 'flex', alignItems: 'center' }}
            whileHover={{ scale: 1.01 }}
          >
            <input
              type="checkbox"
              id="hasBasement"
              checked={formData.hasBasement}
              onChange={(e) => updateFormData({ hasBasement: e.target.checked })}
              style={{
                width: '20px',
                height: '20px',
                cursor: 'pointer',
                accentColor: dpsTheme.colors.accent.blue.primary
              }}
            />
            <label htmlFor="hasBasement" style={{
              marginLeft: dpsTheme.spacing.md,
              fontSize: dpsTheme.typography.fontSize.base,
              color: dpsTheme.colors.text.secondary,
              cursor: 'pointer'
            }}>
              Sous-sol ou cave
            </label>
          </motion.div>

          <motion.div
            style={{ display: 'flex', alignItems: 'center' }}
            whileHover={{ scale: 1.01 }}
          >
            <input
              type="checkbox"
              id="hasGarage"
              checked={formData.hasGarage}
              onChange={(e) => updateFormData({
                hasGarage: e.target.checked,
                garageConnectedToHome: e.target.checked ? formData.garageConnectedToHome : null
              })}
              style={{
                width: '20px',
                height: '20px',
                cursor: 'pointer',
                accentColor: dpsTheme.colors.accent.blue.primary
              }}
            />
            <label htmlFor="hasGarage" style={{
              marginLeft: dpsTheme.spacing.md,
              fontSize: dpsTheme.typography.fontSize.base,
              color: dpsTheme.colors.text.secondary,
              cursor: 'pointer'
            }}>
              Garage
            </label>
          </motion.div>

          {formData.housingType === 'apartment' && formData.hasGarage && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              style={{
                marginLeft: '34px',
                padding: dpsTheme.spacing.lg,
                backgroundColor: dpsTheme.colors.background.darker,
                borderRadius: dpsTheme.borderRadius.md,
                borderLeft: `3px solid ${dpsTheme.colors.accent.blue.primary}`
              }}
            >
              <label style={{
                display: 'block',
                fontSize: dpsTheme.typography.fontSize.sm,
                fontWeight: dpsTheme.typography.fontWeight.semibold,
                color: dpsTheme.colors.text.secondary,
                marginBottom: dpsTheme.spacing.sm
              }}>
                Votre garage est-il directement accessible depuis votre appartement ?
              </label>

              <p style={{
                fontSize: dpsTheme.typography.fontSize.xs,
                color: dpsTheme.colors.text.muted,
                marginBottom: dpsTheme.spacing.md
              }}>
                Un garage connecté possède un accès intérieur direct vers votre logement
              </p>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: dpsTheme.spacing.md
              }}>
                {[
                  { value: true, label: 'Oui, accès intérieur', Icon: DoorOpen },
                  { value: false, label: 'Non, parking séparé', Icon: ParkingSquare }
                ].map((option) => {
                  const isSelected = formData.garageConnectedToHome === option.value;
                  return (
                    <motion.button
                      key={String(option.value)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => updateFormData({ garageConnectedToHome: option.value })}
                      style={{
                        padding: isMobile ? dpsTheme.spacing.md : dpsTheme.spacing.lg,
                        borderRadius: dpsTheme.borderRadius.sm,
                        border: `2px solid ${isSelected ? dpsTheme.colors.accent.blue.primary : dpsTheme.colors.border.primary}`,
                        background: isSelected ? dpsTheme.colors.accent.blue.bg : dpsTheme.colors.background.primary,
                        color: isSelected ? dpsTheme.colors.accent.blue.primary : dpsTheme.colors.text.secondary,
                        fontSize: isMobile ? dpsTheme.typography.fontSize.xs : dpsTheme.typography.fontSize.sm,
                        fontWeight: dpsTheme.typography.fontWeight.medium,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: dpsTheme.spacing.xs,
                        minHeight: isMobile ? '70px' : 'auto'
                      }}
                    >
                      <option.Icon size={isMobile ? 16 : 18} />
                      <span>{option.label}</span>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {formData.housingType === 'house' && (
            <motion.div
              style={{ display: 'flex', alignItems: 'flex-start' }}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <input
                type="checkbox"
                id="hasAccessibleGarden"
                checked={formData.hasAccessibleGarden}
                onChange={(e) => updateFormData({ hasAccessibleGarden: e.target.checked })}
                style={{
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer',
                  accentColor: dpsTheme.colors.accent.blue.primary,
                  marginTop: '2px'
                }}
              />
              <label htmlFor="hasAccessibleGarden" style={{
                marginLeft: dpsTheme.spacing.md,
                fontSize: dpsTheme.typography.fontSize.base,
                color: dpsTheme.colors.text.secondary,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <span>Jardin facilement accessible depuis la rue</span>
                <p style={{
                  fontSize: dpsTheme.typography.fontSize.xs,
                  color: dpsTheme.colors.text.muted,
                  marginTop: '4px'
                }}>
                  Le jardin est considéré comme facilement accessible s'il n'y a pas de clôture haute
                  ou si l'accès n'est pas sécurisé (portail bas, haie basse, etc.)
                </p>
              </label>
            </motion.div>
          )}

          <motion.div
            style={{ display: 'flex', alignItems: 'flex-start' }}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <input
              type="checkbox"
              id="hasAccessibleBalcony"
              checked={formData.hasAccessibleBalcony}
              onChange={(e) => updateFormData({ hasAccessibleBalcony: e.target.checked })}
              style={{
                width: '20px',
                height: '20px',
                cursor: 'pointer',
                accentColor: dpsTheme.colors.accent.blue.primary,
                marginTop: '2px'
              }}
            />
            <label htmlFor="hasAccessibleBalcony" style={{
              marginLeft: dpsTheme.spacing.md,
              fontSize: dpsTheme.typography.fontSize.base,
              color: dpsTheme.colors.text.secondary,
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <span>Balcon facilement accessible depuis l'extérieur</span>
              <p style={{
                fontSize: dpsTheme.typography.fontSize.xs,
                color: dpsTheme.colors.text.muted,
                marginTop: '4px'
              }}>
                Le balcon est considéré comme facilement accessible s'il est situé au rez-de-chaussée
                ou accessible facilement depuis le sol (échelle, arbre, mur d'escalade, etc.)
              </p>
            </label>
          </motion.div>
        </div>

        {!isValid && (
          <p style={{
            color: dpsTheme.colors.priority.high.text,
            fontSize: dpsTheme.typography.fontSize.sm,
            marginTop: dpsTheme.spacing.md
          }}>
            Veuillez sélectionner un type de logement, votre statut d'occupation et indiquer le nombre d'étages
          </p>
        )}
      </div>
    </div>
  );
}